// The staff directory's sums (07 §7.6): time of service, end-of-service pay, visa renewals and the
// monthly payroll. Pure; money is integer minor units. The labour-law rules are an estimate for
// planning, not legal advice: the owner's accountant confirms each real settlement.
//
//   UAE  Federal Decree-Law 33/2021 art. 51: from one year, 21 days' basic pay a year for the first
//        five years, 30 days a year after; at most two years' pay. A day is basic / 30.
//   KSA  Labour Law art. 84 and 85: half a month's wage a year for the first five years, a month a
//        year after (wage = basic + housing). On resignation: none under 2 years, a third from 2,
//        two thirds from 5, all from 10.
//   QA   Labour Law 14/2004 art. 54: from one year, three weeks' basic pay a year. A day is basic / 30.
//   PH   Labor Code art. 298 (separation for an authorised cause): a month's pay a year, at least one
//        month, six months or more counting as a year. RA 7641 (retirement): 22.5 days a year at
//        basic / 26 a day. Nothing on resignation.

export const LABOUR_LAWS = ['UAE', 'KSA', 'QA', 'PH', 'none'] as const;
export type LabourLaw = (typeof LABOUR_LAWS)[number];

export const LEAVE_REASONS = ['resigned', 'terminated', 'end_of_contract', 'retired'] as const;
export type LeaveReason = (typeof LEAVE_REASONS)[number];

const DAY_MS = 86_400_000;

const toDay = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

export interface ServiceLength {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  /** Total days / 365, for the per-year formulas. */
  yearsDecimal: number;
}

/** `date` moved by whole months, kept on the last day of a shorter month (31 Jan + 1 = 28 Feb). */
function addMonths(date: Date, months: number): Date {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth() + months;
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m, Math.min(date.getUTCDate(), last)));
}

/**
 * Whole years, months and days from the first day of work to `until` (both `YYYY-MM-DD`). The
 * decimal years count whole years and months as such, so a leap day never adds to a year.
 */
export function serviceLength(start: string, until: string): ServiceLength {
  const a = new Date(toDay(start));
  const b = new Date(toDay(until));
  if (b.getTime() <= a.getTime())
    return { years: 0, months: 0, days: 0, totalDays: 0, yearsDecimal: 0 };
  let total = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
  if (addMonths(a, total).getTime() > b.getTime()) total -= 1;
  const days = Math.round((b.getTime() - addMonths(a, total).getTime()) / DAY_MS);
  const years = Math.floor(total / 12);
  const months = total % 12;
  const totalDays = Math.round((b.getTime() - a.getTime()) / DAY_MS);
  return { years, months, days, totalDays, yearsDecimal: years + months / 12 + days / 365 };
}

/** "3 years, 2 months", "5 months", "12 days". */
export function serviceText(s: Pick<ServiceLength, 'years' | 'months' | 'days'>): string {
  const part = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
  const parts = [
    s.years > 0 ? part(s.years, 'year') : '',
    s.months > 0 ? part(s.months, 'month') : '',
  ].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return part(s.days, 'day');
}

export interface PayLine {
  kind: string;
  amountMinor: number;
}

export interface Pay {
  basicMinor: number;
  allowances: readonly PayLine[];
}

/** Basic plus every allowance, a month. */
export function monthlyTotalMinor(pay: Pay): number {
  return pay.basicMinor + pay.allowances.reduce((n, a) => n + a.amountMinor, 0);
}

export interface EndOfService {
  amountMinor: number;
  /** How it was worked out, in plain words. */
  basis: string;
  law: LabourLaw;
}

const housingOf = (pay: Pay): number =>
  pay.allowances.filter((a) => a.kind === 'housing').reduce((n, a) => n + a.amountMinor, 0);

/**
 * What a person would be owed at the end of service, leaving on `until` for `reason`, under the
 * labour law of the country that employs them. An estimate; the accountant confirms.
 */
export function endOfService(
  law: LabourLaw,
  pay: Pay,
  start: string,
  until: string,
  reason: LeaveReason,
): EndOfService {
  const s = serviceLength(start, until);
  const y = s.yearsDecimal;
  const none = (basis: string): EndOfService => ({ amountMinor: 0, basis, law });
  switch (law) {
    case 'UAE': {
      if (y < 1) return none('Under one year of service: no gratuity.');
      const day = pay.basicMinor / 30;
      const raw = (Math.min(y, 5) * 21 + Math.max(0, y - 5) * 30) * day;
      const cap = pay.basicMinor * 24;
      return {
        amountMinor: Math.round(Math.min(raw, cap)),
        basis: `21 days' basic a year for the first 5 years, 30 days after${raw > cap ? ', capped at two years’ pay' : ''}.`,
        law,
      };
    }
    case 'KSA': {
      const wage = pay.basicMinor + housingOf(pay);
      const full = (Math.min(y, 5) * 0.5 + Math.max(0, y - 5)) * wage;
      let share = 1;
      if (reason === 'resigned') share = y < 2 ? 0 : y < 5 ? 1 / 3 : y < 10 ? 2 / 3 : 1;
      if (share === 0) return none('Resigned under two years: no end-of-service award.');
      return {
        amountMinor: Math.round(full * share),
        basis: `Half a month's basic + housing a year for the first 5 years, a month after${share < 1 ? `; resigned, so ${share === 1 / 3 ? 'a third' : 'two thirds'} of it` : ''}.`,
        law,
      };
    }
    case 'QA': {
      if (y < 1) return none('Under one year of service: no gratuity.');
      return {
        amountMinor: Math.round(y * 21 * (pay.basicMinor / 30)),
        basis: "Three weeks' basic a year.",
        law,
      };
    }
    case 'PH': {
      if (reason === 'resigned') return none('Resigned: no separation pay under the Labor Code.');
      if (reason === 'retired') {
        return {
          amountMinor: Math.round(Math.floor(y) * 22.5 * (pay.basicMinor / 26)),
          basis: 'Retirement pay: 22.5 days a year at basic / 26 a day.',
          law,
        };
      }
      const years = Math.max(1, s.years + (s.months >= 6 ? 1 : 0));
      return {
        amountMinor: pay.basicMinor * years,
        basis:
          "Separation pay for an authorised cause: a month's basic a year, at least one month.",
        law,
      };
    }
    case 'none':
      return none('No labour-law rule set for this person.');
  }
}

export type ExpiryState = 'none' | 'expired' | 'due_30' | 'due_90' | 'ok';

/** Where a visa or permit stands on `today`: expired, due within 30 or 90 days, or fine. */
export function expiryState(expiresOn: string | undefined, today: string): ExpiryState {
  if (!expiresOn) return 'none';
  const days = Math.round((toDay(expiresOn) - toDay(today)) / DAY_MS);
  if (days < 0) return 'expired';
  if (days <= 30) return 'due_30';
  if (days <= 90) return 'due_90';
  return 'ok';
}

/** Days from `today` to `date` (negative once passed). */
export function daysToDate(date: string, today: string): number {
  return Math.round((toDay(date) - toDay(today)) / DAY_MS);
}
